/**
 * UpgradeGateListener — global handler for the `plan:upgrade-required` event that
 * apiClient dispatches on a 402 PLAN_UPGRADE_REQUIRED. Shows an MUI dialog with
 * the feature, the required plan, and a link to /billing — so a hard API block
 * always reads as a purposeful upsell rather than a raw error. Mount once, inside
 * the Router (needs navigate).
 */
import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
} from "@mui/material";
import { WorkspacePremium as UpgradeIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const UpgradeGateListener = () => {
  const { t } = useTranslation("billing");
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // { feature, requiredPlanLabel, message }

  useEffect(() => {
    const onGate = (e) => setInfo(e.detail || {});
    window.addEventListener("plan:upgrade-required", onGate);
    return () => window.removeEventListener("plan:upgrade-required", onGate);
  }, []);

  const close = () => setInfo(null);
  const goBilling = () => { close(); navigate("/billing"); };

  return (
    <Dialog
      open={!!info}
      onClose={close}
      maxWidth="xs"
      fullWidth
      // Pin an opaque paper so the modal never reads as translucent against the
      // dark theme (MUI's dark Paper adds an elevation backgroundImage overlay).
      PaperProps={{ sx: { bgcolor: "background.paper", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UpgradeIcon color="warning" /> {t("upgradeGate.title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {info?.message || t("upgradeGate.defaultMessage")}
        </Typography>
        {info?.requiredPlanLabel && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans
                i18nKey="upgradeGate.availableOn"
                ns="billing"
                values={{ plan: info.requiredPlanLabel }}
                components={[<strong key="0" />]}
              />
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} color="inherit">{t("upgradeGate.notNow")}</Button>
        <Button onClick={goBilling} variant="contained" startIcon={<UpgradeIcon />}>{t("upgradeGate.viewPlans")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeGateListener;
